'use client';

import { useEffect, useTransition } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/button';
import { Card, CardContent } from '@/components/card';
import { calSans } from '@/lib/fonts';
import { cn } from '@/lib/utils';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const router = useRouter();
  const [retrying, startRetry] = useTransition();

  useEffect(() => {
    console.error('Application error:', error);
  }, [error]);

  // `reset()` alone only re-renders the errored boundary against the SAME cached
  // (failed) server render — so after a transient network/DB blip "Try again"
  // appears to do nothing. Refresh the route first to refetch server data, then
  // reset the boundary. (Next.js app-router error-recovery pattern.)
  const handleRetry = () => {
    startRetry(() => {
      router.refresh();
      reset();
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card variant="elevated" className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-[rgb(var(--error)/0.1)] border border-[rgb(var(--error)/0.3)] flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-[rgb(var(--error))]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className={cn("text-2xl font-bold text-[rgb(var(--foreground))] mb-2", calSans.className)}>
            Something went wrong
          </h2>
          <p className={cn("text-[rgb(var(--foreground-secondary))] mb-6 break-words whitespace-pre-wrap", calSans.className)}>
            {error.message || 'An unexpected error occurred. Please try again.'}
          </p>
          <div className="flex items-center justify-center space-x-4">
            <Button variant="primary" onClick={handleRetry} disabled={retrying} className={calSans.className}>
              {retrying ? 'Retrying…' : 'Try again'}
            </Button>
            <Button variant="ghost" onClick={() => window.location.href = '/'} className={calSans.className}>
              Go home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

